import React, { Fragment } from 'react';
import { IconButton, Badge, Typography, AppBar, Toolbar } from '@material-ui/core';
import PropTypes from 'prop-types';
import { ROLES, GLOBAL_NAVIGATION_TABS } from 'constants/index';
import { useSubscription } from '@apollo/client';
import { getNotificationsCounter } from 'utils/commonUtils';
import { useConversationSubscribe } from 'modules/shell/hooks/useConversationSubscribe';
import {
  Conversations,
  ConversationsSelected,
  GlobalSearch,
  GlobalSearchSelected,
  HomeIcon,
  HomeIconSelected,
  ClientsSelectedIcon,
  ClientsIcon2,
  StyleIcon,
  StyleSelectedIcon,
} from 'sharedComponents/Icons';
import { useCalendarRange } from 'modules/shell/hooks/useCalendarRange';
import {
  SubscribeToAssociateMessageNotificationCreated,
  SubscribeToAssociateMessageNotificationUpdated,
  SubscribeToAssociateMessageMark,
} from '../../../../graphQL/subscription';

const getOptions = (associateId, subscription, cb) => ({
  variables: { associateId },
  shouldResubscribe: true,
  onSubscriptionData: ({ subscriptionData: { data } }) => {
    if (!data) {
      return null;
    }
    return cb(data[subscription]);
  },
});

const MessageSubscribe = ({ associateId, handler, setError }) => {
  const { error: createdError } = useSubscription(
    SubscribeToAssociateMessageNotificationCreated,
    getOptions(associateId, 'onAssociateMessageNotificationCreated', handler),
  );

  const { error: addedError } = useSubscription(
    SubscribeToAssociateMessageNotificationUpdated,
    getOptions(associateId, 'onAssociateMessageNotificationUpdated', handler),
  );

  const { error: readError } = useSubscription(
    SubscribeToAssociateMessageMark,
    getOptions(associateId, 'onAssociateMessageMark', handler),
  );

  React.useEffect(() => setError(addedError), [addedError]);
  React.useEffect(() => setError(createdError), [createdError]);
  React.useEffect(() => setError(readError), [readError]);

  return null;
};

const GlobalNav = ({
  classes,
  history,
  location,
  user,
  isVisible,
  notifications,
  refetchMessageCount,
  closeStylyzeApp,
  isBackOfHouseUser,
}) => {
  const [subscriptionError, setError] = useConversationSubscribe();
  const [activeTab, setActiveTab] = React.useState('more');
  const [messageNotifications, setMessageNotifications] = React.useState(notifications);
  const [previousPath, setPreviousPath] = React.useState(location.pathname);
  const { resetDate } = useCalendarRange();
  const updateMessages = (newNotification) => {
    const restMessages = messageNotifications.filter((message) => message.contactId !== newNotification.contactId);
    setMessageNotifications([newNotification, ...restMessages]);
    refetchMessageCount();
  };

  React.useEffect(() => {
    setMessageNotifications(notifications);
  }, [notifications]);

  React.useEffect(() => {
    refetchMessageCount();
  }, [isVisible]);

  React.useEffect(() => {
    const currentTab = Object.entries(GLOBAL_NAVIGATION_TABS).find(([, value]) =>
      location.pathname.startsWith(value.path),
    );

    const [key] = currentTab || ['more'];

    setActiveTab(key);
  }, [location.pathname]);

  const onClick = async (tabName, postMutation) => {
    await closeStylyzeApp();
    if (tabName === 'alerts') {
      postMutation({
        variables: { associateId: user.employeeNumber },
        update: () => {
          history.push(GLOBAL_NAVIGATION_TABS.alerts.path);
        },
      });
    }
    if (tabName === 'messages') {
      refetchMessageCount();
    }

    setPreviousPath(GLOBAL_NAVIGATION_TABS[tabName].path);

    if (previousPath === GLOBAL_NAVIGATION_TABS.messages.path) {
      refetchMessageCount();
    }

    if (tabName !== activeTab && tabName === 'clients') {
      return history.push(GLOBAL_NAVIGATION_TABS[tabName].path, {
        isGlobalNavEntry: true,
      });
    }

    if (tabName !== activeTab && tabName !== 'alerts') {
      return history.push(GLOBAL_NAVIGATION_TABS[tabName].path);
    }

    // Return user to root location of active module
    // Specially for AC-4 of https://neimanmarcus.atlassian.net/browse/OSP-5285
    if (tabName === activeTab) {
      history.replace(GLOBAL_NAVIGATION_TABS[tabName].path, {
        resetEntries: true,
      });
    }
    // Reset Performance context provider (CalendarRange) to initial state
    // when clicking 'Home' while in Home module
    if (tabName === 'home' && activeTab === 'home') {
      resetDate();
    }
  };

  const stylist = user.role === ROLES.STYLIST || user.role === ROLES.TESTER;

  const renderMessagesTabIcon = (icon) => {
    const notificationsCount = getNotificationsCounter(messageNotifications);

    return (
      <Badge
        badgeContent={notificationsCount}
        color="primary"
        classes={{ badge: classes.customBadge }}
        overlap="rectangular"
      >
        {icon}
      </Badge>
    );
  };

  const navBarProps = [
    {
      page: 'home',
      selected: <HomeIconSelected />,
      unSelected: <HomeIcon className={classes.semiTransparent} />,
      caption: 'Home',
      onClick: () => onClick('home'),
    },
    {
      page: 'search',
      selected: <GlobalSearchSelected />,
      unSelected: <GlobalSearch className={classes.semiTransparent} />,
      caption: 'Search',
      onClick: () => onClick('search'),
    },
    {
      page: 'clients',
      selected: <ClientsSelectedIcon />,
      unSelected: <ClientsIcon2 className={classes.semiTransparent} />,
      caption: 'Clients',
      onClick: () => onClick('clients'),
      hidden: isBackOfHouseUser,
    },
    {
      page: 'style',
      selected: <StyleSelectedIcon />,
      unSelected: <StyleIcon className={classes.semiTransparent} />,
      caption: 'Style',
      onClick: () => onClick('style'),
    },
    {
      page: 'messages',
      selected: renderMessagesTabIcon(<ConversationsSelected />),
      unSelected: renderMessagesTabIcon(<Conversations className={classes.semiTransparent} />),
      caption: 'Convos',
      onClick: () => onClick('messages'),
      hidden: isBackOfHouseUser,
    },
  ];
  const activeNavBarProps = navBarProps.filter(({ hidden }) => !hidden);

  return (
    <>
      {isVisible && stylist ? (
        <>
          <AppBar position="fixed" color="primary" className={classes.appBar}>
            <Toolbar className={classes.toolbar}>
              {activeNavBarProps.map((item) => {
                const { page, selected, unSelected, caption, onClick } = item;
                return (
                  <Fragment key={page}>
                    <IconButton
                      aria-label={`${page}page`}
                      data-testid={`${page}-page`}
                      onClick={onClick}
                      classes={{ label: classes.iconLabel }}
                    >
                      {activeTab === page ? selected : unSelected}
                      <Typography variant="caption" color={activeTab === page ? 'textPrimary' : 'textSecondary'}>
                        {caption}
                      </Typography>
                    </IconButton>
                  </Fragment>
                );
              })}
            </Toolbar>
          </AppBar>
          <div className={classes.offset} />
        </>
      ) : null}
      {!subscriptionError && (
        <MessageSubscribe associateId={user.employeeNumber} handler={updateMessages} setError={setError} />
      )}
    </>
  );
};

GlobalNav.propTypes = {
  user: PropTypes.shape({
    role: PropTypes.string,
    employeeNumber: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    logged: PropTypes.bool.isRequired,
  }),
  classes: PropTypes.objectOf(PropTypes.string),
  location: PropTypes.shape({}),
  isVisible: PropTypes.bool,
  refetchMessageCount: PropTypes.func,
};

GlobalNav.defaultProps = {
  classes: {},
  user: {
    role: '',
    employeeNumber: '',
    firstName: '',
    lastName: '',
    logged: false,
  },
  location: {},
  isVisible: true,
  refetchMessageCount: () => null,
};

export default GlobalNav;
